import axios from 'axios';
import { createLogger } from '../utils/debugLogger';

const API_URL = process.env.REACT_APP_API_URL;

// Create debug logger for this module
const logger = createLogger('api', 'HTTP API requests and responses', false, 'INFO');
logger.info('API service initialized with URL:', API_URL);

// Create axios instance with default config
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true // Enable sending cookies with requests
});

// Debugging log for each request
api.interceptors.request.use(
  (config) => {
    logger.debug(`API request to: ${config.baseURL}${config.url}`);
    const token = localStorage.getItem('token');
    if (token) {
      logger.debug(`Adding token to ${config.url} request (token length: ${token.length})`);
      config.headers['Authorization'] = `Bearer ${token}`;
    } else {
      logger.debug(`No token found for ${config.url} request`);
    }
    return config;
  },
  (error) => {
    logger.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor to handle errors
const PIPELINE_URL_RE = /\/api\/(basing|vcp|watchlist|readylist|leaders|eps)\//;
api.interceptors.response.use(
  (response) => {
    if (response.config.method !== 'get' && PIPELINE_URL_RE.test(response.config.url)) {
      window.dispatchEvent(new CustomEvent('pipeline:changed'));
    }
    return response;
  },
  (error) => {
    // Don't redirect on 401 during login attempts
    const isLoginRequest = error.config?.url?.includes('/api/users/login');
    // Don't redirect if we're already on the login page to prevent refresh loops
    const isOnLoginPage = window.location.pathname === '/login';

    if (error.response?.status === 401 && !isLoginRequest && !isOnLoginPage) {
      // Handle unauthorized access (except during login or if already on login page)
      localStorage.removeItem('token');
      localStorage.removeItem('userId');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Authentication API calls
export const login = (username, password) => {
  return api.post('/api/users/login', { username, password });
};

export const register = (username, email, password, invitationToken) => {
  const payload = { username, email, password };
  if (invitationToken) {
    payload.invitation_token = invitationToken;
  }
  return api.post('/api/users/register', payload);
};

export const getUserProfile = () => {
  return api.get('/api/users/profile');
};

export const isPremiumUser = () => {
  const subscriptionPlan = localStorage.getItem('subscriptionPlan');
  return subscriptionPlan === 'premium';
};

export const updateProfile = (userData) => {
  return api.put('/api/users/profile', userData);
};

// Portfolios API calls
export const getPortfolios = (userId) => {
  return api.get(`/api/portfolios/?user_id=${userId}`);
};

export const getPortfolio = (portfolioId) => {
  return api.get(`/api/portfolios/${portfolioId}`);
};

export const createPortfolio = (portfolioData) => {
  return api.post('/api/portfolios/', portfolioData);
};

export const updatePortfolio = (portfolioId, portfolioData) => {
  return api.put(`/api/portfolios/${portfolioId}`, portfolioData);
};

export const deletePortfolio = (portfolioId) => {
  return api.delete(`/api/portfolios/${portfolioId}`);
};

// Accounts API calls
export const getAccounts = (portfolioId) => {
  return api.get(`/api/accounts/?portfolio_id=${portfolioId}`);
};

export const getAllAccounts = () => {
  return api.get('/api/accounts/all');
};

export const getAllAccountsWithAggregate = () => {
  return api.get('/api/accounts/all-with-aggregate');
};

export const getAccount = (accountId) => {
  return api.get(`/api/accounts/${accountId}`);
};

export const createAccount = (accountData) => {
  return api.post('/api/accounts/', accountData);
};

export const updateAccount = (accountId, accountData) => {
  return api.put(`/api/accounts/${accountId}`, accountData);
};

export const deleteAccount = (accountId) => {
  return api.delete(`/api/accounts/${accountId}`);
};

export const getTodayGainIntradayHistory = (days = 1) => {
  return api.get(`/api/accounts/today-gain-intraday?days=${days}`);
};

export const recordTodayGainIntradaySnapshot = (gainAmount) => {
  return api.post('/api/accounts/today-gain-intraday', { gain_amount: gainAmount });
};

export const getAccountTodayGainIntradayExtremes = (days = 1) => {
  return api.get(`/api/accounts/today-gain-intraday/accounts?days=${days}`);
};

// Tickers API calls
export const getTickers = () => {
  return api.get('/api/tickers/')
    .then(response => {
      // Handle the updated response format 
      if (response.data && response.data.tickers) {
        // New format with market info
        return {
          ...response,
          data: response.data.tickers,
          marketInfo: {
            market_closed: response.data.market_closed,
            market_info: response.data.market_info
          }
        };
      }
      // Old format or fallback
      return response;
    });
};

export const getAccountTickers = (accountId) => {
  return api.get(`/api/tickers/account/${accountId}`);
};

export const getOrderDropdownTickers = () => {
  return api.get('/api/tickers/order-dropdown');
};

export const getAggregatedTickers = () => {
  return api.get('/api/tickers/aggregated');
};

export const getVersionCommits = () => {
  return api.get('/api/version/commits');
};
export const getTickerPrice = (symbol) => {
  return api.get(`/api/tickers/price/${symbol}`);
};

export const updateTickerPrices = (forceUpdate = false) => {
  return api.post('/api/tickers/update', { force_update: forceUpdate })
    .then(response => {
      // Dispatch tickerUpdate event if update was successful
      if (response.data && (
        // Check several possible response formats
        response.data.success > 0 || 
        response.data.updated_tickers?.length > 0 ||
        response.data.updated_tickers_count > 0
      )) {
        // PRIORITIZE ACTUAL DATABASE TIMESTAMPS FROM TICKERS
        let updateTime;
        
        // First try to get the most recent timestamp directly from the ticker data
        if (response.data.updated_tickers && response.data.updated_tickers.length > 0) {
          // Find the most recent ticker timestamp
          let mostRecentDate = null;

          for (const ticker of response.data.updated_tickers) {
            if (ticker.last_updated) {
              try {
                const tickerDate = new Date(ticker.last_updated);
                if (!isNaN(tickerDate) && tickerDate <= new Date()) {
                  if (!mostRecentDate || tickerDate > mostRecentDate) {
                    mostRecentDate = tickerDate;
                  }
                }
              } catch (e) {
                // Error parsing timestamp
              }
            }
          }

          if (mostRecentDate) {
            updateTime = mostRecentDate;
          }
        }
        
        // Fallback to latest_update_time if no valid ticker timestamp found
        if (!updateTime && response.data.latest_update_time) {
          updateTime = new Date(response.data.latest_update_time);
        }
        
        // Last resort - use current time
        if (!updateTime || isNaN(updateTime.getTime())) {
          updateTime = new Date();
        }
        
        // Check if updateTime is valid
        if (isNaN(updateTime.getTime())) {
          updateTime = new Date();
        }
        
        // Use CustomEvent to pass the timestamp
        window.dispatchEvent(new CustomEvent('tickerUpdate', { 
          detail: { 
            time: updateTime,
            source: 'api.updateTickerPrices',
            serverTimestamp: updateTime !== new Date(),
            original: updateTime.toISOString()
          } 
        }));
      }
      return response;
    });
};

// Screens API
export const getScreenState = () => api.get('/api/screens/');

export const createScreen = (name) => api.post('/api/screens/', { name });

export const renameScreen = (screenId, name) =>
  api.put(`/api/screens/${screenId}`, { name });

export const deleteScreen = (screenId) => api.delete(`/api/screens/${screenId}`);

export const reorderScreens = (order) => api.post('/api/screens/reorder', { order });

export const addScreenTicker = (symbol) => api.post('/api/screens/tickers', { symbol });

export const deleteScreenTicker = (tickerId) =>
  api.delete(`/api/screens/tickers/${tickerId}`);

export const starScreenTicker = (tickerId, screenId) =>
  api.post(`/api/screens/tickers/${tickerId}/stars/${screenId}`);

export const unstarScreenTicker = (tickerId, screenId) =>
  api.delete(`/api/screens/tickers/${tickerId}/stars/${screenId}`);

export const createTicker = (tickerData) => {
  return api.post('/api/tickers/', tickerData);
};

export const updateAccountTickerArmor = (accountId, tickerId, armor) => {
  return api.post(`/api/tickers/account/${accountId}/ticker/${tickerId}/armor`, { armor });
};

// Transactions API calls
export const getTransactions = (accountId) => {
  return api.get(`/api/transactions/?account_id=${accountId}`);
};

export const getAllTransactions = (page = 1, limit = 50, filters = {}) => {
  let url = `/api/transactions/all?page=${page}&limit=${limit}`;
  
  // Add filters to the query string if provided
  if (filters.symbol) url += `&symbol=${filters.symbol}`;
  if (filters.accountId) url += `&account_id=${filters.accountId}`;
  if (filters.type) url += `&type=${filters.type}`;
  if (filters.startDate) url += `&start_date=${filters.startDate}`;
  if (filters.endDate) url += `&end_date=${filters.endDate}`;
  if (filters.stopoutOnly) url += `&is_stopout=true`;
  
  // Add sorting parameters if provided
  if (filters.sort_by) url += `&sort_by=${filters.sort_by}`;
  if (filters.sort_order) url += `&sort_order=${filters.sort_order}`;
  
  return api.get(url);
};

export const createTransaction = (transactionData) => {
  return api.post('/api/transactions/', transactionData);
};

export const deleteTransaction = (transactionId) => {
  return api.delete(`/api/transactions/${transactionId}`);
};

export const updateTransaction = (transactionId, transactionData) => {
  return api.put(`/api/transactions/${transactionId}`, transactionData);
};

export const getTickerTransactions = (symbol, accountId = null) => {
  const params = accountId ? { account_id: accountId } : {};
  return api.get(`/api/transactions/ticker/${symbol}`, { params });
};

// Search API calls
export const searchTickerAcrossAccounts = (ticker) => {
  return api.get(`/api/search/ticker/${ticker}`);
};
export const searchTickerInPipeline = (ticker) => {
  return api.get(`/api/search/ticker/${ticker}/pipeline`);
};

// Categories API calls
export const getCategories = () => {
  logger.debug('getCategories called with baseURL:', api.defaults.baseURL);
  return api.get('/api/categories/');
};

export const getCategory = (categoryId) => {
  return api.get(`/api/categories/${categoryId}/`);
};

export const createCategory = (categoryData) => {
  return api.post('/api/categories/', categoryData);
};

export const updateCategory = (categoryId, categoryData) => {
  return api.put(`/api/categories/${categoryId}/`, categoryData);
};

export const updateTickerCategory = (tickerId, categoryId) => {
  return api.put(`/api/tickers/${tickerId}/category`, {
    category_id: categoryId
  });
};

export const updateAccountTickerCategory = (accountId, tickerId, categoryId) => {
  return api.put(`/api/accounts/${accountId}/tickers/${tickerId}/category`, {
    category_id: categoryId
  });
};

export const updateAccountTickerEntryRating = (accountId, tickerId, entryRating) => {
  return api.put(`/api/tickers/${tickerId}/account/${accountId}/entry-rating`, {
    entry_rating: entryRating
  });
};

// Account history API calls
export const saveAccountHistory = (accountId) => {
  return api.post(`/api/accounts/${accountId}/save-history`);
};

export const saveAllAccountsHistory = () => {
  return api.post('/api/accounts/save-all-history');
};

export const getAccountHistory = (accountId, params = {}) => {
  return api.get(`/api/accounts/${accountId}/history`, { params });
};

export const getAggregatedAccountHistory = (params = {}) => {
  return api.get('/api/accounts/history/aggregated', { params });
};

// System Activities API calls
export const getSystemActivities = (params = {}) => {
  return api.get('/api/system-activities/', { params });
};

// ========================================
// Diagnostic functions for debugging
// ========================================

export const getDiagnosticData = async (accountId) => {
  try {
    // Get account details directly
    const accountResponse = await api.get(`/api/accounts/${accountId}`);
    
    // Get account from accounts list endpoint
    const portfolioId = accountResponse.data.portfolio_id;
    const accountsListResponse = await api.get(`/api/accounts/?portfolio_id=${portfolioId}`);
    
    // Find this account in the accounts list
    const accountInList = accountsListResponse.data.find(a => a.account_id === parseInt(accountId));
    
    // Get portfolio details
    const portfolioResponse = await api.get(`/api/portfolios/${portfolioId}`);
    
    return {
      account: accountResponse.data,
      accountInList: accountInList,
      portfolio: portfolioResponse.data,
      accountsList: accountsListResponse.data
    };
  } catch (error) {
    logger.error("Error fetching diagnostic data:", error);
    throw error;
  }
};

// Settings API
export const getSettings = () => {
  return api.get('/api/settings');
};

export const updateSettings = (settings) => {
  logger.debug(`Updating settings with URL: ${api.defaults.baseURL}/api/settings`);
  logger.debug('Settings data:', settings);
  return api.put('/api/settings', settings, {
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

export const getTopNavigationLinks = () => {
  return api.get('/api/navigation/top-links');
};

export const updateTopNavigationLinks = (payload) => {
  return api.put('/api/navigation/top-links', payload, {
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

// Market Condition API calls
export const getMarketCondition = () => {
  return api.get('/api/market-condition');
};

export const updateMarketCondition = (marketCondition) => {
  return api.put('/api/market-condition', { market_condition: marketCondition });
};

// Market Indexes API
export const getMarketIndexes = () => {
  return api.get('/api/tickers/market-indexes');
};

export const updateMarketIndexes = (forceUpdate = true) => {
  return api.post('/api/tickers/update-indexes', { force_update: forceUpdate })
    .then(response => {
      // Dispatch tickerUpdate event if update was successful
      if (response.data && (
        response.data.success > 0 ||
        response.data.updated_tickers?.length > 0 ||
        response.data.updated_tickers_count > 0
      )) {
        logger.info('Market indexes updated:', response.data);
        window.dispatchEvent(new CustomEvent('indexUpdate', {
          detail: {
            time: new Date(),
            source: 'api.updateMarketIndexes'
          }
        }));
      }
      return response;
    });
};

// Admin Settings API calls
export const getAdminSettings = () => {
  return api.get('/api/admin/settings');
};

export const updateAdminSettings = (settings) => {
  return api.put('/api/admin/settings', settings);
};

// Market Index Selection API
export const saveSelectedMarketIndexes = (selectedIndexes) => {
  return api.post('/api/admin/selected-market-indexes', { selected_indexes: selectedIndexes });
};

export const getSelectedMarketIndexes = () => {
  return api.get('/api/admin/selected-market-indexes');
};

export const checkAdminStatus = () => {
  return api.get('/api/admin/check');
};

// Weather API
export const weatherApi = {
  getCurrent: (city, units = 'imperial') =>
    api.get(`/api/weather/current?city=${encodeURIComponent(city)}&units=${units}`)
};

export const getConnectionStatus = () => {
  return api.get('/api/tickers/connection-status')
    .catch(error => {
      logger.error('Error fetching connection status:', error);
      return { data: { success: false, error: error.message } };
    });
};

// Ticker validation API
export const validateTicker = (symbol) => {
  return api.post('/api/tickers/validate', { symbol });
};

// Livermore API
export const getLivermoreTickers = () => {
  return api.get('/api/livermore/tickers');
};

export const addLivermoreTicker = (payload) => {
  return api.post('/api/livermore/tickers', payload);
};

export const updateLivermoreTicker = (livermoreTickerId, payload) => {
  return api.put(`/api/livermore/tickers/${livermoreTickerId}`, payload);
};

export const deleteLivermoreTicker = (livermoreTickerId) => {
  return api.delete(`/api/livermore/tickers/${livermoreTickerId}`);
};

export const getLivermoreRecords = (livermoreTickerId, page = 1, pageSize = 20) => {
  return api.get(`/api/livermore/tickers/${livermoreTickerId}/records`, {
    params: { page, page_size: pageSize, _ts: Date.now() },
    headers: { 'Cache-Control': 'no-cache' }
  });
};

// Play API
export const getPlayPage = () => api.get('/api/play/');
export const updatePlayPage = (content) => api.put('/api/play/', { content });

// Get selected status bar tickers (market indexes + custom tickers)
export const getSelectedStatusTickers = () => {
  return api.get('/api/tickers/selected-status-tickers');
};

export const getLastUpdateTime = () => {
  return api.get('/api/tickers/last-update-time')
    .catch(error => {
      logger.error('Error fetching last update time:', error);
      // In case of error, fetch a list of tickers as fallback
      return getTickers().then(response => {
        // Process tickers to find most recent update
        const tickers = response.data || [];
        let mostRecentTime = null;

        for (const ticker of tickers) {
          if (ticker.last_updated) {
            try {
              const tickerDate = new Date(ticker.last_updated);
              if (!isNaN(tickerDate) && tickerDate <= new Date()) {
                if (!mostRecentTime || tickerDate > new Date(mostRecentTime)) {
                  mostRecentTime = ticker.last_updated;
                  logger.debug('Found valid DB timestamp from ticker:', ticker.symbol, mostRecentTime);
                }
              }
            } catch (e) {
              logger.error('Error parsing ticker timestamp:', e);
            }
          }
        }

        if (!mostRecentTime) {
          logger.warn('No valid ticker timestamps found for last update time');
        }

        return {
          data: { 
            last_update_time: mostRecentTime,
            source: mostRecentTime ? 'ticker_fallback' : 'none'
          }
        };
      });
    });
};

// Trades API calls
export const getTrades = (params = {}) => {
  return api.get('/api/trades', { params });
};

export const getTradeDetails = (tradeId) => {
  return api.get(`/api/trades/${tradeId}`);
};

export const getAdjacentUnanalyzedTrade = (params = {}) => {
  return api.get('/api/trades/next-unanalyzed', { params });
};

export const createTrade = (tradeData) => {
  return api.post('/api/trades', tradeData);
};

export const updateTrade = (tradeId, tradeData) => {
  return api.put(`/api/trades/${tradeId}`, tradeData);
};

export const deleteTrade = (tradeId) => {
  return api.delete(`/api/trades/${tradeId}`);
};

export const getGainLossDistribution = (forceRefresh = false) => {
  return api.get('/api/trades/gain-loss-distribution', { 
    params: { force_refresh: forceRefresh } 
  });
};

export const getTradingStatistics = (params = {}) => {
  return api.get('/api/trades/statistics', { params });
};

// Open (unsold) BUY lots for a ticker+account's active trade, in LIFO order.
export const getOpenLots = (tickerId, accountId) => {
  return api.get(`/api/tickers/${tickerId}/accounts/${accountId}/open-lots`);
};

export const addTradeShield = (tradeId, shieldData) => {
  return api.post(`/api/trades/${tradeId}/shields`, shieldData);
};

export const updateTradeShield = (tradeId, shieldId, shieldData) => {
  return api.put(`/api/trades/${tradeId}/shields/${shieldId}`, shieldData);
};

export const deleteTradeShield = (tradeId, shieldId) => {
  return api.delete(`/api/trades/${tradeId}/shields/${shieldId}`);
};

// Ticker Shield APIs
export const getTickerShields = (tickerId, accountId, transactionId = null) => {
  const query = transactionId !== null ? `?transaction_id=${transactionId}` : '';
  return api.get(`/api/tickers/${tickerId}/accounts/${accountId}/shields${query}`);
};

export const commitShieldsToBroker = (tickerId, accountId, isCommitted) => {
  return api.post(`/api/tickers/${tickerId}/accounts/${accountId}/shields/commit`, {
    is_committed_to_broker: isCommitted
  });
};

export const recalculateAccountShields = (accountId) => {
  return api.post(`/api/accounts/${accountId}/shields/recalculate`);
};

export const addTickerShield = (tickerId, accountId, shieldData) => {
  return api.post(`/api/tickers/${tickerId}/accounts/${accountId}/shields`, shieldData);
};

export const updateTickerShield = (tickerId, accountId, shieldId, shieldData) => {
  return api.put(`/api/tickers/${tickerId}/accounts/${accountId}/shields/${shieldId}`, shieldData);
};

export const deleteTickerShield = (tickerId, accountId, shieldId) => {
  return api.delete(`/api/tickers/${tickerId}/accounts/${accountId}/shields/${shieldId}`);
};

export const deleteAllTickerShields = (tickerId, accountId) => {
  return api.delete(`/api/tickers/${tickerId}/accounts/${accountId}/shields`);
};

export const getTrailingStopInfo = (tickerId, accountId, shieldId) => {
  return api.get(`/api/tickers/${tickerId}/accounts/${accountId}/shields/${shieldId}/trailing-info`);
};

export const autoCreateTradeFromTransaction = (transactionData) => {
  return api.post('/api/trades/auto-create-from-transaction', transactionData);
};

export const getTradeShieldSummary = (tradeId) => {
  return api.get(`/api/trades/${tradeId}/shield-summary`);
};

// Daily Statistics API calls
export const generateTradeDailyStatistics = (tradeId, dateRange = {}) => {
  return api.post(`/api/trades/${tradeId}/daily-statistics`, dateRange);
};

export const backfillDailyStatistics = (daysBack = 30) => {
  return api.post('/api/trades/daily-statistics/backfill', { days_back: daysBack });
};

export const generateAllTradeDailyStatistics = () => {
  return api.post('/api/trades/daily-statistics/generate-all');
};

// Intraday Price Data API calls
export const getTickerIntradayData = (symbol, date = null, limit = 1000) => {
  const params = new URLSearchParams();
  if (date) params.append('date', date);
  if (limit) params.append('limit', limit);
  
  return api.get(`/api/tickers/${symbol}/intraday?${params.toString()}`);
};

export const getTickerDailySummary = (symbol, date = null) => {
  const params = new URLSearchParams();
  if (date) params.append('date', date);
  
  return api.get(`/api/tickers/${symbol}/daily-summary?${params.toString()}`);
}; 

// Orders API calls
export const createOrder = (accountId, orderData) => {
  return api.post(`/api/accounts/${accountId}/orders`, orderData);
};

export const getAccountOrders = (accountId) => {
  return api.get(`/api/accounts/${accountId}/orders`);
};

export const updateOrder = (orderId, orderData) => {
  return api.put(`/api/orders/${orderId}`, orderData);
};

export const commitOrder = (orderId) => {
  return api.post(`/api/orders/${orderId}/commit`);
};

export const fulfillOrder = (orderId) => {
  return api.post(`/api/orders/${orderId}/fulfill`);
};

export const deleteOrder = (orderId) => {
  return api.delete(`/api/orders/${orderId}`);
};

export const getAllOrders = (page = 1, limit = 50, filters = {}) => {
  // Filters: account_id, symbol, status, type, start_date, end_date
  const params = new URLSearchParams();
  params.append('page', page);
  params.append('limit', limit);
  Object.entries(filters).forEach(([key, value]) => {
    if (value) params.append(key, value);
  });
  return api.get(`/api/orders?${params.toString()}`);
}; 

// Watchlist API
export const getWatchlist = () => api.get('/api/watchlist/');
export const addWatchlistTicker = (ticker, notes = '', options = {}) => {
  const payload = { ticker, notes };

  if (Object.prototype.hasOwnProperty.call(options, 'includeInPriceUpdates')) {
    payload.include_in_price_updates = options.includeInPriceUpdates;
  }

  return api.post('/api/watchlist/', payload);
};
export const deleteWatchlistTicker = (id) => api.delete(`/api/watchlist/${id}`); 
export const updateWatchlistCategory = (id, category_id) =>
  api.put(`/api/watchlist/${id}`, { category_id });

export const updateWatchlistItem = (id, data) =>
  api.put(`/api/watchlist/${id}`, data);

// Ants API
export const getAnts = () => api.get('/api/ants/');
export const addAntTicker = (ticker, notes = '') => api.post('/api/ants/', { ticker, notes });
export const deleteAntTicker = (id) => api.delete(`/api/ants/${id}`);
export const updateAntCategory = (id, category_id) =>
  api.put(`/api/ants/${id}`, { category_id });
export const updateAntItem = (id, data) =>
  api.put(`/api/ants/${id}`, data);
export const promoteAntToReadylist = (itemId) => api.post(`/api/ants/${itemId}/promote`);
export const promoteAntToLeaders = (itemId) => api.post(`/api/ants/${itemId}/promote-to-leaders`);

// New IPOs API
export const getNewIpos = () => api.get('/api/new-ipos/');
export const addNewIpoTicker = (ticker, notes = '') => api.post('/api/new-ipos/', { ticker, notes });
export const deleteNewIpoTicker = (id) => api.delete(`/api/new-ipos/${id}`);
export const updateNewIpoCategory = (id, category_id) =>
  api.put(`/api/new-ipos/${id}`, { category_id });
export const updateNewIpoItem = (id, data) =>
  api.put(`/api/new-ipos/${id}`, data);
export const promoteNewIpoToReadylist = (itemId) => api.post(`/api/new-ipos/${itemId}/promote`);
export const promoteNewIpoToLeaders = (itemId) => api.post(`/api/new-ipos/${itemId}/promote-to-leaders`);

// Stopouts API
export const getStopouts = () => api.get('/api/stopouts/');
export const addStopoutTicker = (ticker, notes = '') => api.post('/api/stopouts/', { ticker, notes });
export const deleteStopoutTicker = (id) => api.delete(`/api/stopouts/${id}`); 
export const updateStopoutCategory = (id, category_id) =>
  api.put(`/api/stopouts/${id}`, { category_id });

export const updateStopoutItem = (id, data) =>
  api.put(`/api/stopouts/${id}`, data);
export const promoteStopoutToReadylist = (itemId) => api.post(`/api/stopouts/${itemId}/promote`);
export const promoteStopoutToLeaders = (itemId) => api.post(`/api/stopouts/${itemId}/promote-to-leaders`);

// ETFs API
export const getEtfs = () => api.get('/api/etfs/');
export const addEtfTicker = (ticker, notes = '') => api.post('/api/etfs/', { ticker, notes });
export const deleteEtfTicker = (id) => api.delete(`/api/etfs/${id}`); 
export const updateEtfCategory = (id, category_id) =>
  api.put(`/api/etfs/${id}`, { category_id });

export const updateEtfItem = (id, data) =>
  api.put(`/api/etfs/${id}`, data);
export const promoteEtfToReadylist = (itemId) => api.post(`/api/etfs/${itemId}/promote`);
export const promoteEtfToLeaders = (itemId) => api.post(`/api/etfs/${itemId}/promote-to-leaders`);

// ATHigh (All Time High) API
export const getATHigh = () => api.get('/api/athigh/');
export const addATHighTicker = (ticker, notes = '') => api.post('/api/athigh/', { ticker, notes });
export const deleteATHighTicker = (id) => api.delete(`/api/athigh/${id}`);
export const updateATHighCategory = (id, category_id) =>
  api.put(`/api/athigh/${id}`, { category_id });
export const updateATHighItem = (id, data) =>
  api.put(`/api/athigh/${id}`, data);
export const promoteATHighToReadylist = (itemId) => api.post(`/api/athigh/${itemId}/promote`);
export const promoteATHighToLeaders = (itemId) => api.post(`/api/athigh/${itemId}/promote-to-leaders`);

// Turnarounds API
export const getTurnarounds = () => api.get('/api/turnarounds/');
export const addTurnaroundTicker = (ticker, notes = '') => api.post('/api/turnarounds/', { ticker, notes });
export const deleteTurnaroundTicker = (id) => api.delete(`/api/turnarounds/${id}`);
export const updateTurnaroundCategory = (id, category_id) =>
  api.put(`/api/turnarounds/${id}`, { category_id });
export const updateTurnaroundItem = (id, data) =>
  api.put(`/api/turnarounds/${id}`, data);
export const promoteTurnaroundToReadylist = (itemId) => api.post(`/api/turnarounds/${itemId}/promote`);
export const promoteTurnaroundToLeaders = (itemId) => api.post(`/api/turnarounds/${itemId}/promote-to-leaders`);

// Cyclicals API
export const getCyclicals = () => api.get('/api/cyclicals/');
export const addCyclicalTicker = (ticker, notes = '') => api.post('/api/cyclicals/', { ticker, notes });
export const deleteCyclicalTicker = (id) => api.delete(`/api/cyclicals/${id}`);
export const updateCyclicalCategory = (id, category_id) =>
  api.put(`/api/cyclicals/${id}`, { category_id });
export const updateCyclicalItem = (id, data) =>
  api.put(`/api/cyclicals/${id}`, data);
export const promoteCyclicalToReadylist = (itemId) => api.post(`/api/cyclicals/${itemId}/promote`);
export const promoteCyclicalToLeaders = (itemId) => api.post(`/api/cyclicals/${itemId}/promote-to-leaders`);

// Leaders API
export const getLeaders = () => api.get('/api/leaders/');
export const addLeaderTicker = (ticker, notes = '') => api.post('/api/leaders/', { ticker, notes });
export const deleteLeaderTicker = (id) => api.delete(`/api/leaders/${id}`);
export const updateLeaderCategory = (id, category_id) =>
  api.put(`/api/leaders/${id}`, { category_id });

export const updateLeaderItem = (id, data) =>
  api.put(`/api/leaders/${id}`, data);

// EPS API
export const getEpsItems = () => api.get('/api/eps/');
export const addEpsTicker = (ticker, notes = '') => api.post('/api/eps/', { ticker, notes });
export const deleteEpsTicker = (id) => api.delete(`/api/eps/${id}`);
export const updateEpsCategory = (id, category_id) =>
  api.put(`/api/eps/${id}`, { category_id });

export const updateEpsItem = (id, data) =>
  api.put(`/api/eps/${id}`, data);

// VCP API
export const getVcpItems = () => api.get('/api/vcp/');
export const addVcpTicker = (ticker, notes = '') => api.post('/api/vcp/', { ticker, notes });
export const deleteVcpTicker = (id) => api.delete(`/api/vcp/${id}`);
export const updateVcpCategory = (id, category_id) =>
  api.put(`/api/vcp/${id}`, { category_id });

export const updateVcpItem = (id, data) =>
  api.put(`/api/vcp/${id}`, data);

// Basing API
export const getBasingItems = () => api.get('/api/basing/');
export const addBasingTicker = (ticker, notes = '') => api.post('/api/basing/', { ticker, notes });
export const deleteBasingTicker = (id) => api.delete(`/api/basing/${id}`);
export const updateBasingCategory = (id, category_id) =>
  api.put(`/api/basing/${id}`, { category_id });

export const updateBasingItem = (id, data) =>
  api.put(`/api/basing/${id}`, data);

export const getTradingPlan = () => api.get('/api/trading-plan/', { responseType: 'text' });

// Readylist API
export const getReadylist = () => api.get('/api/readylist/');
export const addReadylistTicker = (ticker, notes = '') => api.post('/api/readylist/', { ticker, notes });
export const deleteReadylistTicker = (id) => api.delete(`/api/readylist/${id}`); 
export const updateReadylistCategory = (id, category_id) =>
  api.put(`/api/readylist/${id}`, { category_id });

export const updateReadylistItem = (id, data) =>
  api.put(`/api/readylist/${id}`, data);

// Promote/Downgrade API
export const promoteToReadylist = (watchlistId) => api.post(`/api/watchlist/${watchlistId}/promote`);
export const promoteLeaderToReadylist = (leaderId) => api.post(`/api/leaders/${leaderId}/promote`);
export const promoteToLeaders = (watchlistId) => api.post(`/api/watchlist/${watchlistId}/promote-to-leaders`);
export const downgradeToWatchlist = (readylistId) => api.post(`/api/readylist/${readylistId}/downgrade`);
export const demoteLeaderToWatchlist = (leaderId) => api.post(`/api/leaders/${leaderId}/demote-to-watchlist`);
export const demoteWatchlistToBasing = (watchlistId) => api.post(`/api/watchlist/${watchlistId}/demote-to-basing`);
export const promoteEpsToReadylist = (epsId) => api.post(`/api/eps/${epsId}/promote`);
export const demoteEpsToWatchlist = (epsId) => api.post(`/api/eps/${epsId}/demote-to-watchlist`);
export const promoteVcpToReadylist = (vcpId) => api.post(`/api/vcp/${vcpId}/promote`);
export const demoteVcpToBasing = (vcpId) => api.post(`/api/vcp/${vcpId}/demote-to-basing`);
export const promoteBasingToVcp = (basingId) => api.post(`/api/basing/${basingId}/promote`);

// Pipeline counts (single lightweight call for all badge counts)
export const getPipelineCounts = () => api.get('/api/pipeline/counts');

// Ticker Notes API
export const getTickerNotes = (tickerId) => api.get(`/api/tickers/${tickerId}/notes`);
export const addTickerNote = (tickerId, note) => api.post(`/api/tickers/${tickerId}/notes`, { note });
export const editTickerNote = (noteId, note) => api.put(`/api/tickers/notes/${noteId}`, { note });
export const deleteTickerNote = (noteId) => api.delete(`/api/tickers/notes/${noteId}`);
export const togglePinNote = (noteId) => api.put(`/api/tickers/notes/${noteId}/pin`);
export const getAllTickerNotes = (page = 1, limit = 10, symbol = '') => {
  const params = new URLSearchParams();
  params.append('page', page);
  params.append('limit', limit);
  if (symbol) params.append('symbol', symbol);
  return api.get(`/api/tickers/notes/all?${params.toString()}`);
};

// Ticker Rating API calls
export const getTickerRating = (tickerId) => api.get(`/api/tickers/${tickerId}/rating`);
export const updateTickerRating = (tickerId, rating) => api.put(`/api/tickers/${tickerId}/rating`, { rating });

export const getTickerTSize = (tickerId) => api.get(`/api/tickers/${tickerId}/tsize`);
export const updateTickerTSize = (tickerId, tsize) => api.put(`/api/tickers/${tickerId}/tsize`, { tsize });
export const updateAccountTickerTSize = (tickerId, accountId, tsize) => api.put(`/api/tickers/${tickerId}/account/${accountId}/tsize`, { tsize });

// Next plan APIs
export const updateAccountTickerNextPlan = (tickerId, accountId, payload) =>
  api.put(`/api/tickers/${tickerId}/account/${accountId}/next-plan`, payload);

// Theme APIs
export const updateAccountTickerTheme = (accountId, tickerId, themeEnabled) =>
  api.put(`/api/accounts/${accountId}/tickers/${tickerId}/theme`, { theme_enabled: themeEnabled });

export const updateAccountTickerBase = (accountId, tickerId, baseEnabled) =>
  api.put(`/api/accounts/${accountId}/tickers/${tickerId}/base`, { base_enabled: baseEnabled });

// Manual price APIs
export const setTickerManualPrice = (tickerId, price) =>
  api.post(`/api/tickers/set-manual-price/${tickerId}`, { price });

export const clearTickerManualPrice = (tickerId) =>
  api.post(`/api/tickers/clear-manual-price/${tickerId}`);

// Wisdom APIs
export const getWisdom = (page = 1, limit = 10, q = '') => {
  const params = new URLSearchParams();
  params.append('page', page);
  params.append('limit', limit);
  if (q) params.append('q', q);
  return api.get(`/api/wisdom/?${params.toString()}`);
};
export const createWisdom = (title, contents) => api.post('/api/wisdom/', { title, contents });
export const updateWisdom = (id, data) => api.put(`/api/wisdom/${id}`, data);
export const deleteWisdom = (id) => api.delete(`/api/wisdom/${id}`);

// Strategy APIs
export const getStrategies = (page = 1, limit = 10, q = '') => {
  const params = new URLSearchParams();
  params.append('page', page);
  params.append('limit', limit);
  if (q) params.append('q', q);
  return api.get(`/api/strategies/?${params.toString()}`);
};
export const createStrategy = (name, entry_conditions, trading_plan) => api.post('/api/strategies/', { name, entry_conditions, trading_plan });
export const updateStrategy = (id, data) => api.put(`/api/strategies/${id}`, data);
export const deleteStrategy = (id) => api.delete(`/api/strategies/${id}`);

// Theme APIs
export const getThemes = (page = 1, limit = 10, q = '') => {
  const params = new URLSearchParams();
  params.append('page', page);
  params.append('limit', limit);
  if (q) params.append('q', q);
  return api.get(`/api/themes/?${params.toString()}`);
};
export const createTheme = (name, market_sentiment, tickers) => api.post('/api/themes/', { name, market_sentiment, tickers });
export const updateTheme = (id, data) => api.put(`/api/themes/${id}`, data);
export const deleteTheme = (id) => api.delete(`/api/themes/${id}`);

// Theme ticker APIs
export const getThemeTickers = (themeName) => api.get(`/api/themes/${encodeURIComponent(themeName)}/tickers`);
export const addThemeTicker = (themeName, ticker, data = {}) =>
  api.post(`/api/themes/${encodeURIComponent(themeName)}/tickers`, { ticker, ...data });
export const updateThemeTicker = (themeName, id, data) =>
  api.put(`/api/themes/${encodeURIComponent(themeName)}/tickers/${id}`, data);
export const deleteThemeTicker = (themeName, id) =>
  api.delete(`/api/themes/${encodeURIComponent(themeName)}/tickers/${id}`);
export const promoteThemeTicker = (themeName, id) =>
  api.post(`/api/themes/${encodeURIComponent(themeName)}/tickers/${id}/promote`);
export const promoteThemeTickerToLeaders = (themeName, id) =>
  api.post(`/api/themes/${encodeURIComponent(themeName)}/tickers/${id}/promote-to-leaders`);

// GLeaders APIs
export const getGLeaders = (page = 1, limit = 10, q = '') => {
  const params = new URLSearchParams();
  params.append('page', page);
  params.append('limit', limit);
  if (q) params.append('q', q);
  return api.get(`/api/gleaders/?${params.toString()}`);
};
export const createGLeader = (name, market_sentiment, tickers, pinned = false) =>
  api.post('/api/gleaders/', { name, market_sentiment, tickers, pinned });
export const updateGLeader = (id, data) => api.put(`/api/gleaders/${id}`, data);
export const deleteGLeader = (id) => api.delete(`/api/gleaders/${id}`);
export const getGLeaderTickers = (groupName) => api.get(`/api/gleaders/${encodeURIComponent(groupName)}/tickers`);
export const addGLeaderTicker = (groupName, ticker, data = {}) =>
  api.post(`/api/gleaders/${encodeURIComponent(groupName)}/tickers`, { ticker, ...data });
export const updateGLeaderTicker = (groupName, id, data) =>
  api.put(`/api/gleaders/${encodeURIComponent(groupName)}/tickers/${id}`, data);
export const deleteGLeaderTicker = (groupName, id) =>
  api.delete(`/api/gleaders/${encodeURIComponent(groupName)}/tickers/${id}`);
export const promoteGLeaderTicker = (groupName, id) =>
  api.post(`/api/gleaders/${encodeURIComponent(groupName)}/tickers/${id}/promote`);
export const promoteGLeaderTickerToLeaders = (groupName, id) =>
  api.post(`/api/gleaders/${encodeURIComponent(groupName)}/tickers/${id}/promote-to-leaders`);

// Inverses API
export const getInverses = () => api.get('/api/inverses/');
export const addInverseTicker = (ticker, notes = '') => api.post('/api/inverses/', { ticker, notes });
export const deleteInverseTicker = (id) => api.delete(`/api/inverses/${id}`);
export const updateInverseCategory = (id, category_id) =>
  api.put(`/api/inverses/${id}`, { category_id });
export const updateInverseItem = (id, data) =>
  api.put(`/api/inverses/${id}`, data);

// Steroids API
export const getSteroids = () => api.get('/api/steroids/');
export const addSteroidTicker = (ticker, notes = '') => api.post('/api/steroids/', { ticker, notes });
export const deleteSteroidTicker = (id) => api.delete(`/api/steroids/${id}`);
export const updateSteroidCategory = (id, category_id) =>
  api.put(`/api/steroids/${id}`, { category_id });
export const updateSteroidItem = (id, data) =>
  api.put(`/api/steroids/${id}`, data);

// HVol API
export const getHVols = () => api.get('/api/hvols/');
export const addHVolTicker = (ticker, notes = '') => api.post('/api/hvols/', { ticker, notes });
export const deleteHVolTicker = (id) => api.delete(`/api/hvols/${id}`);
export const updateHVolCategory = (id, category_id) =>
  api.put(`/api/hvols/${id}`, { category_id });
export const updateHVolItem = (id, data) =>
  api.put(`/api/hvols/${id}`, data);
export const promoteHVolToReadylist = (itemId) => api.post(`/api/hvols/${itemId}/promote`);
export const promoteHVolToLeaders = (itemId) => api.post(`/api/hvols/${itemId}/promote-to-leaders`);

// Minervini API
export const getMinervini = () => api.get('/api/minervini/');
export const addMinerviniTicker = (ticker, notes = '') => api.post('/api/minervini/', { ticker, notes });
export const deleteMinerviniTicker = (id) => api.delete(`/api/minervini/${id}`);
export const updateMinerviniCategory = (id, category_id) =>
  api.put(`/api/minervini/${id}`, { category_id });
export const updateMinerviniItem = (id, data) =>
  api.put(`/api/minervini/${id}`, data);

// Column Preferences API
export const getColumnPreferences = (pageType) => api.get(`/api/column-preferences/${pageType}`);
export const saveColumnPreferences = (pageType, visibleColumns, columnOrder) => 
  api.post(`/api/column-preferences/${pageType}`, { visible_columns: visibleColumns, column_order: columnOrder });
export const resetColumnPreferences = (pageType) => api.delete(`/api/column-preferences/${pageType}`);
export const getAllColumnPreferences = () => api.get('/api/column-preferences');

// System Monitoring APIs
export const getSystemResources = () => api.get('/api/admin/monitor/system-resources');
export const getApplicationHealth = () => api.get('/api/admin/monitor/application-health');
export const getPerformanceMetrics = () => api.get('/api/admin/monitor/performance');

// Backend Debug APIs
export const getDebugSwitches = () => api.get('/api/admin/debug/switches');
export const updateDebugSwitches = (switches) => api.put('/api/admin/debug/switches', { switches });
export const getRecentLogs = () => api.get('/api/admin/debug/logs');

// Thread Manager APIs
export const getThreads = () => api.get('/api/admin/threads');
export const getThreadHealth = () => api.get('/api/admin/threads/health');
export const learnThreads = () => api.post('/api/admin/threads/learn');

// Thread Control APIs
export const controlThread = (threadId, threadName, action, confirmSafety = false) => 
  api.post('/api/admin/threads/control', { 
    thread_id: threadId, 
    thread_name: threadName, 
    action, 
    confirm_safety: confirmSafety 
  });
export const getControllableServices = () => api.get('/api/admin/threads/services');
export const controlService = (serviceName, action, confirmSafety = false) => 
  api.post(`/api/admin/threads/services/${serviceName}`, { 
    action, 
    confirm_safety: confirmSafety 
  });
